// ============================================================
// Süper admin — kreş başvuruları (§2)
//
// Birinci kapı: kreş açmak isteyen Google ile girer, başvurusu
// burada birikir. Onaylayan kiracıyı, planı ve şubeleri BURADA
// belirler — başvuru formunda o alanlar yok, çünkü plan ticari bir
// karar ve başvurana bırakılırsa herkes kendine kurumsal yazar.
//
// Bu sekme yalnızca gerçek kurulumda (firebaseHazir) anlamlı;
// demo kurulumda Firestore yok, başvuru da yok.
// ============================================================

import { card, emptyNote, sectionTitle } from "../components/ui.js";
import { esc } from "../lib/fmt.js";
import { PLANLAR, PLAN_SIRASI } from "../domain/billing.js";
import { firebaseHazir } from "../config.js";
import { rerender } from "../lib/rerender.js";

// Firestore'dan gelen başvurular. Görünümler eşzamanlı çalışıyor;
// veri gelince rerender ile ikinci kez çiziliyor.
let onbellek = null;
let yukleniyor = false;
let hata = null;

export const basvuruListesi = () => onbellek ?? [];

async function yukle() {
  if (yukleniyor) return;
  yukleniyor = true;
  hata = null;
  try {
    const { basvurulariOku } = await import("../domain/firebase-auth.js");
    onbellek = await basvurulariOku();
  } catch (e) {
    hata = e.message;
    onbellek = [];
  } finally {
    yukleniyor = false;
    rerender();
  }
}

/** Onay/ret sonrası listeyi tazelemek için. */
export function basvurulariTazele() {
  onbellek = null;
  yukle();
}

export function basvuruSekmesi() {
  if (!firebaseHazir) {
    return emptyNote("Başvurular yalnızca gerçek kurulumda görünür — bu tarayıcıda demo veri çalışıyor.");
  }

  if (onbellek === null) {
    yukle();
    return card(`<div class="p-6 text-sm text-inksoft">Başvurular yükleniyor…</div>`);
  }

  if (hata) {
    return card(`<div class="p-6 text-sm text-coral font-semibold">Başvurular okunamadı: ${esc(hata)}</div>`);
  }

  if (!onbellek.length) {
    return emptyNote("Bekleyen kreş başvurusu yok.");
  }

  return `
    ${sectionTitle("Bekleyen başvurular", `${onbellek.length} kurum onay bekliyor`)}
    <div class="space-y-4">
      ${onbellek.map(satir).join("")}
    </div>`;
}

function satir(b) {
  // Şube kimliği kurum adından türetilir ama düzenlenebilir — kimlik
  // yola giriyor ve sonradan değiştirilemiyor, onaylayan görsün.
  const varsayilanId = kimlikOner(b.kurumAdi);

  return card(`
    <form data-basvuru="${esc(b.id)}" class="p-5">
      <div class="flex items-start justify-between gap-4 mb-4">
        <div class="min-w-0">
          <div class="font-display font-bold text-base truncate">${esc(b.kurumAdi ?? "—")}</div>
          <div class="text-xs text-inksoft truncate">${esc(b.name ?? "")} · ${esc(b.email ?? "")}</div>
          ${b.telefon ? `<div class="text-xs text-inksoft">${esc(b.telefon)}</div>` : ""}
        </div>
      </div>

      ${b.not ? `<div class="rounded-xl bg-paper p-3 text-sm text-inksoft mb-4">${esc(b.not)}</div>` : ""}

      <div class="grid gap-3 sm:grid-cols-2 mb-4">
        <label class="block">
          <span class="block text-[11px] font-bold uppercase tracking-wider text-inksoft mb-1">Kiracı kimliği</span>
          <input name="tenantId" required value="${esc(varsayilanId)}" pattern="[a-z0-9-]{2,40}"
            class="w-full rounded-xl border border-line px-3 py-2 text-sm font-mono">
          <span class="block text-[10px] text-inksoft/80 mt-1">Yola giriyor, sonradan değişmez.</span>
        </label>

        <label class="block">
          <span class="block text-[11px] font-bold uppercase tracking-wider text-inksoft mb-1">Plan</span>
          <select name="planId" class="w-full rounded-xl border border-line px-3 py-2 text-sm">
            ${PLAN_SIRASI.map(id => `
              <option value="${id}" ${id === "baslangic" ? "selected" : ""}>${esc(PLANLAR[id].label)}</option>`).join("")}
          </select>
        </label>
      </div>

      <div class="mb-4">
        <span class="block text-[11px] font-bold uppercase tracking-wider text-inksoft mb-1">Şubeler</span>
        <textarea name="subeler" rows="2"
          class="w-full rounded-xl border border-line px-3 py-2 text-sm"
          placeholder="Her satıra bir şube: merkez | Merkez Şube">merkez | Merkez Şube</textarea>
        <span class="block text-[10px] text-inksoft/80 mt-1">
          Satır başına <code>kimlik | Görünen ad</code>. Kimlik küçük harf, rakam ve tire.
        </span>
      </div>

      <div class="flex flex-wrap gap-2">
        <button type="submit" data-onay
          class="rounded-xl bg-teal text-white font-semibold text-sm px-4 py-2.5 hover:opacity-90 transition">
          Onayla ve kurumu aç
        </button>
        <button type="button" data-ret="${esc(b.id)}"
          class="rounded-xl border border-line font-semibold text-sm px-4 py-2.5 hover:bg-paper transition">
          Reddet
        </button>
        <span data-basvuru-durum class="text-sm text-inksoft self-center"></span>
      </div>
    </form>`);
}

/** Kurum adından yol-güvenli bir kimlik önerir. */
export function kimlikOner(ad) {
  return (ad ?? "")
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i").replace(/ğ/g, "g").replace(/ü/g, "u")
    .replace(/ş/g, "s").replace(/ö/g, "o").replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40) || "kurum";
}

/** "merkez | Merkez Şube" satırlarını şube nesnelerine çevirir. */
export function subeleriAyristir(metin) {
  return (metin ?? "")
    .split("\n")
    .map(s => s.trim())
    .filter(Boolean)
    .map(satir => {
      const [id, ad] = satir.split("|").map(x => x.trim());
      return { id: id ?? "", name: ad || id || "" };
    })
    .filter(s => s.id);
}
